import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Bot,
  RefreshCw,
  Search,
  X,
  ShieldCheck,
  ShieldAlert,
  Sliders,
  ChevronRight,
  Cpu,
} from "lucide-react";
import { agentsListConfigurations, agentsListModels } from "../gen";
import type {
  AgentConfigurationResponse,
  ModelCatalogResponse,
  ModelCatalogItem,
} from "../gen/models";
import { useOrg } from "../hooks/useOrg";
import { useSearchQuery } from "../hooks/useSearchQuery";
import { AgentConfigDetail } from "./AgentConfigDetail";
import { ScrollableColumnContainer } from "../components/common/ScrollableColumnContainer";
import { TimeAgo } from "../components/common/TimeAgo";
import { getModelIntelligence } from "../utils/modelInfo";

import { normalizeAgentType, sanitizeProtocoloName } from "../hooks/useUserDirectory";

const KNOWN_AGENTS_METADATA: Record<
  string,
  { name: string; role: string; description: string; defaultModel: string }
> = {
  frontdesk: {
    name: "Sofia",
    role: "Front Desk & Operations",
    description: "HubSpot live chat, resident concierge, check-in logistics",
    defaultModel: "gemini-3.8-flash",
  },
  el_protocolo: {
    name: "Sofia",
    role: "Front Desk & Operations",
    description: "HubSpot live chat, resident concierge, check-in logistics",
    defaultModel: "gemini-3.8-flash",
  },
  protocolo: {
    name: "Sofia",
    role: "Front Desk & Operations",
    description: "HubSpot live chat, resident concierge, check-in logistics",
    defaultModel: "gemini-3.8-flash",
  },
  sales: {
    name: "Don Silvestre",
    role: "Quotes & Luxury Proposals",
    description: "Seasonal rates, villa stays, and Instagram leads",
    defaultModel: "gemini-3.8-flash",
  },
  biblioteca: {
    name: "La Biblioteca",
    role: "Conversational Analytics",
    description: "SQL metrics, Superset warehouse data, leads & revenue",
    defaultModel: "gemini-3.8-flash",
  },
  feedback: {
    name: "Feedback Curation",
    role: "Quality Assurance & Rules",
    description: "Thread evaluations, batch review, alignment proposals",
    defaultModel: "gemini-3.1-flash-lite",
  },
  email_drafting: {
    name: "Email Drafter",
    role: "Gmail Inbound Concierge",
    description: "Personalized inbound email drafts, tone calibration, and CRM context",
    defaultModel: "gemini-3.8-flash",
  },
  template: {
    name: "Template Agent",
    role: "Workflow Prototype",
    description: "Persistent telemetry, operational rules, and reference lookup",
    defaultModel: "gemini-3.8-flash",
  },
};

function formatInOutPrice(promptPrice: number, completionPrice: number): string {
  return `$${promptPrice.toFixed(2)} in / $${completionPrice.toFixed(2)} out`;
}

export function AgentConfigWorkspace() {
  const { agent_type } = useParams<{ agent_type?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { activeOrgId } = useOrg();

  const [configs, setConfigs] = useState<AgentConfigurationResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Models catalog for dynamic pricing & labels
  const [modelCatalog, setModelCatalog] = useState<ModelCatalogResponse | null>(null);

  useEffect(() => {
    async function loadModels() {
      try {
        const resp = await agentsListModels();
        if ("data" in resp && resp.status === 200) {
          setModelCatalog(resp.data);
        }
      } catch (err) {
        console.warn("Could not load dynamic models catalog:", err);
      }
    }
    loadModels();
  }, []);

  const allCatalogModels = useMemo(() => {
    if (!modelCatalog) return undefined;
    const map = new Map<string, ModelCatalogItem>();
    if (modelCatalog.curated_models) {
      for (const m of modelCatalog.curated_models) {
        map.set(m.id, m);
      }
    }
    if (modelCatalog.models) {
      for (const m of modelCatalog.models) {
        map.set(m.id, m);
      }
    }
    return Array.from(map.values());
  }, [modelCatalog]);

  // Search
  const [searchQuery, setSearchQuery] = useSearchQuery("q");

  const loadConfigs = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await agentsListConfigurations();
      const data = (res as any).data;
      if (Array.isArray(data)) {
        setConfigs(data);
      } else {
        setConfigs([]);
      }
    } catch (err: any) {
      setError(err?.message || "Failed to load agent configurations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfigs();
  }, [activeOrgId]);

  const filteredConfigs = configs.filter((c) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    const norm = normalizeAgentType(c.agent_type);
    const meta = KNOWN_AGENTS_METADATA[norm] || KNOWN_AGENTS_METADATA[c.agent_type];
    const name = meta?.name.toLowerCase() || "";
    const role = meta?.role.toLowerCase() || "";
    const type = c.agent_type.toLowerCase();
    const configuredModelId =
      (c.configuration as any)?.model_parameters?.model ||
      meta?.defaultModel ||
      "";
    const modelIntel = getModelIntelligence(configuredModelId, allCatalogModels);
    const modelName = modelIntel.name.toLowerCase();
    const modelId = modelIntel.id.toLowerCase();
    return (
      name.includes(q) ||
      role.includes(q) ||
      type.includes(q) ||
      modelName.includes(q) ||
      modelId.includes(q)
    );
  });

  const hasSelection = Boolean(agent_type);

  return (
    <div className="flex h-full w-full overflow-hidden min-h-0">
      {/* ============================================================== */}
      {/* COLUMN 2: Compact Agent Configurations List Pane               */}
      {/* ============================================================== */}
      <div
        className={`${
          hasSelection ? "hidden md:flex" : "flex"
        } w-full md:w-80 lg:w-[360px] xl:w-96 shrink-0 h-full border-r border-stone-200 dark:border-stone-800 bg-white/70 dark:bg-stone-900/60 flex-col min-h-0 overflow-hidden select-none transition-all`}
      >
        {/* Pane Header */}
        <div className="p-3.5 border-b border-stone-200 dark:border-stone-800 flex items-center justify-between gap-2 shrink-0 bg-stone-50/50 dark:bg-stone-950/50">
          <div className="flex items-center gap-2 min-w-0">
            <h2 className="text-sm font-bold tracking-tight text-stone-900 dark:text-stone-100 truncate">
              Agent Configs
            </h2>
            <span className="px-1.5 py-0.2 rounded-md bg-stone-200 dark:bg-stone-800 text-[10px] font-mono font-semibold text-stone-600 dark:text-stone-400">
              {filteredConfigs.length}
            </span>
          </div>

          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={loadConfigs}
              disabled={loading}
              className="p-1.5 rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 text-stone-600 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-stone-800 transition"
              title="Refresh configurations list"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="p-2.5 border-b border-stone-100 dark:border-stone-800/80 shrink-0">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400 dark:text-stone-500 pointer-events-none" />
            <input
              type="text"
              placeholder="Search agents..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-8 pr-7 py-1.5 text-xs bg-white dark:bg-stone-900 text-stone-900 dark:text-stone-100 placeholder:text-stone-400 dark:placeholder:text-stone-500 border border-stone-200 dark:border-stone-800 focus:outline-hidden focus:ring-1 focus:ring-stone-400 dark:focus:ring-stone-600 font-sans shadow-2xs"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 dark:hover:text-stone-300"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Scrollable Compact List */}
        <ScrollableColumnContainer>
          {loading && configs.length === 0 ? (
            <div className="p-8 text-center text-stone-400 dark:text-stone-500 text-xs flex items-center justify-center gap-2">
              <RefreshCw className="w-4 h-4 animate-spin text-stone-500" />
              <span>Loading configurations...</span>
            </div>
          ) : error ? (
            <div className="p-4 rounded-lg bg-rose-50 dark:bg-rose-950/60 border border-rose-200 dark:border-rose-900 text-rose-700 dark:text-rose-400 text-xs">
              {error}
            </div>
          ) : filteredConfigs.length === 0 ? (
            <div className="p-8 text-center space-y-2">
              <Bot className="w-7 h-7 text-stone-300 dark:text-stone-600 mx-auto" />
              <div className="text-xs font-semibold text-stone-700 dark:text-stone-300">
                No agents found
              </div>
            </div>
          ) : (
            filteredConfigs.map((c) => {
              const isSelected = c.agent_type === agent_type;
              const norm = normalizeAgentType(c.agent_type);
              const meta = KNOWN_AGENTS_METADATA[norm] || KNOWN_AGENTS_METADATA[c.agent_type] || {
                name: sanitizeProtocoloName(c.agent_type.toUpperCase(), "SOFIA"),
                role: "Autonomous Agent",
                description: "",
                defaultModel: "gemini-3.8-flash",
              };
              const gr = (c.configuration as any)?.guardrails;
              const grEnabled = gr?.enabled !== false;
              const isCustomized = c.version > 0;
              const configuredModelId =
                (c.configuration as any)?.model_parameters?.model ||
                meta.defaultModel ||
                "gemini-3.8-flash";
              const modelIntel = getModelIntelligence(
                configuredModelId,
                allCatalogModels
              );
              const priceDisplay = formatInOutPrice(
                modelIntel.promptPrice,
                modelIntel.completionPrice
              );

              const mp = (c.configuration as any)?.model_parameters;
              const timeoutSec = typeof mp?.timeout_seconds === "number" ? mp.timeout_seconds : 180;

              return (
                <div
                  key={c.agent_type}
                  onClick={() => navigate(`/agents/${c.agent_type}${location.search}`)}
                  className={`px-4 py-3 border-b border-l-2 text-xs cursor-pointer transition-all duration-150 space-y-1.5 ${
                    isSelected
                      ? "bg-white dark:bg-stone-900 border-l-stone-900 dark:border-l-stone-100 border-b-stone-200 dark:border-b-stone-800 shadow-2xs opacity-100"
                      : "border-l-transparent border-b-stone-200/80 dark:border-b-stone-800/60 hover:bg-stone-100/70 dark:hover:bg-stone-800/50 opacity-100"
                  }`}
                >
                  <div className="flex items-center justify-between gap-1.5">
                    <div className="flex items-center gap-1.5">
                      <span className="font-semibold text-xs text-stone-900 dark:text-stone-100">
                        {meta.name}
                      </span>
                      <span className="px-1.5 py-0.2 rounded text-[9px] font-mono font-bold uppercase bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400 border border-stone-200 dark:border-stone-700">
                        {c.agent_type}
                      </span>
                    </div>

                    <span
                      className={`px-1.5 py-0.2 rounded-full text-[9px] font-mono font-semibold border ${
                        isCustomized
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950 dark:text-emerald-400 dark:border-emerald-800"
                          : "bg-stone-100 text-stone-600 border-stone-200 dark:bg-stone-800 dark:text-stone-400 dark:border-stone-700"
                      }`}
                    >
                      {isCustomized ? `v${c.version}` : "default"}
                    </span>
                  </div>

                  <p className="line-clamp-1 text-[11px] text-stone-500 dark:text-stone-400 font-sans">
                    {meta.role}
                  </p>

                  {/* Configured Model & In/Out Pricing */}
                  <div className="flex items-center justify-between gap-1.5 text-[11px] font-mono">
                    <div className="flex items-center gap-1.5 min-w-0 truncate text-stone-700 dark:text-stone-300">
                      <Cpu className="w-3.5 h-3.5 shrink-0 text-stone-400 dark:text-stone-500" />
                      <span className="truncate font-medium" title={modelIntel.id}>
                        {modelIntel.name}
                      </span>
                    </div>
                    <span
                      className="shrink-0 text-[10px] text-emerald-600 dark:text-emerald-400 font-semibold bg-emerald-50 dark:bg-emerald-950/40 px-1.5 py-0.5 rounded border border-emerald-200/60 dark:border-emerald-800/40"
                      title="Prompt (input) / Completion (output) price per 1M tokens"
                    >
                      {priceDisplay}
                    </span>
                  </div>

                  {/* Provider Routing Pill if configured */}
                  {(() => {
                    const prov = mp?.provider || mp?.providers;
                    const order = Array.isArray(prov?.order) ? prov.order : Array.isArray(prov) ? prov : [];
                    const latency = prov?.preferred_max_latency;
                    const throughput = prov?.preferred_min_throughput;
                    if (order.length === 0 && !latency && !throughput) return null;
                    return (
                      <div className="flex items-center gap-1.5 text-[10px] font-mono text-stone-500 dark:text-stone-400 truncate">
                        <span className="font-semibold text-stone-600 dark:text-stone-300">Providers:</span>
                        <span className="truncate">
                          {order.length > 0 ? order.slice(0, 2).join(" → ") + (order.length > 2 ? ` +${order.length - 2}` : "") : "Dynamic"}
                        </span>
                        {latency && <span className="text-amber-600 dark:text-amber-400">≤{latency}s</span>}
                        {throughput && <span className="text-emerald-600 dark:text-emerald-400">≥{throughput}t/s</span>}
                      </div>
                    );
                  })()}

                  <div className="flex items-center justify-between text-[10px] text-stone-400 dark:text-stone-500 pt-1 border-t border-stone-100 dark:border-stone-800/60 gap-1.5 font-mono">
                    <div className="flex items-center gap-1.5">
                      {grEnabled ? (
                        <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                          <ShieldCheck className="w-3 h-3" />
                          <span>Guardrails ON</span>
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400">
                          <ShieldAlert className="w-3 h-3" />
                          <span>Guardrails OFF</span>
                        </span>
                      )}
                      {gr?.hard_block_threshold && (
                        <>
                          <span>•</span>
                          <span>≥{Number(gr.hard_block_threshold).toFixed(2)}</span>
                        </>
                      )}
                      <span>•</span>
                      <span>{timeoutSec}s</span>
                    </div>
                    <TimeAgo date={c.updated_at} className="shrink-0 font-mono" />
                  </div>
                </div>
              );
            })
          )}
        </ScrollableColumnContainer>
      </div>

      {/* ============================================================== */}
      {/* COLUMN 3: Detail Workspace Pane                                */}
      {/* ============================================================== */}
      <div
        className={`${
          hasSelection ? "flex" : "hidden md:flex"
        } flex-1 min-w-0 h-full overflow-y-auto [scrollbar-gutter:stable] flex-col bg-stone-50 dark:bg-stone-950 p-4 md:p-6`}
      >
        {agent_type ? (
          <AgentConfigDetail
            key={agent_type}
            agentType={agent_type}
            onConfigUpdated={loadConfigs}
          />
        ) : (
          <div className="h-full w-full flex flex-col items-center justify-center p-8 text-center select-none space-y-6 max-w-2xl mx-auto">
            <div className="w-14 h-14 rounded-2xl bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 flex items-center justify-center shadow-xs">
              <Sliders className="w-7 h-7 text-stone-400 dark:text-stone-500" />
            </div>
            <div className="space-y-1.5">
              <h3 className="text-base font-bold text-stone-900 dark:text-stone-100">
                Agent Dynamic Runtime Configurations
              </h3>
              <p className="text-xs text-stone-500 dark:text-stone-400 leading-relaxed max-w-md mx-auto">
                Fine-tune safety barrier thresholds, on-block action policies, foundation models, and system directives individually per autonomous agent.
              </p>
            </div>

            {/* Quick Select Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full text-left">
              {(configs.length > 0
                ? configs
                : Object.entries(KNOWN_AGENTS_METADATA)
                    .filter(([key]) => key !== "el_protocolo" && key !== "protocolo")
                    .map(([key, item]) => ({
                      agent_type: key,
                      configuration: { model_parameters: { model: item.defaultModel } },
                      version: 0,
                      updated_at: new Date().toISOString(),
                    }))
              ).map((c) => {
                const norm = normalizeAgentType(c.agent_type);
                const meta =
                  KNOWN_AGENTS_METADATA[norm] ||
                  KNOWN_AGENTS_METADATA[c.agent_type] || {
                    name: sanitizeProtocoloName(c.agent_type.toUpperCase(), "SOFIA"),
                    role: "Autonomous Agent",
                    description: "",
                    defaultModel: "gemini-3.8-flash",
                  };
                const configuredModelId =
                  (c.configuration as any)?.model_parameters?.model ||
                  meta.defaultModel ||
                  "gemini-3.8-flash";
                const modelIntel = getModelIntelligence(
                  configuredModelId,
                  allCatalogModels
                );
                const priceDisplay = formatInOutPrice(
                  modelIntel.promptPrice,
                  modelIntel.completionPrice
                );

                return (
                  <div
                    key={c.agent_type}
                    onClick={() => navigate(`/agents/${c.agent_type}`)}
                    className="p-3.5 rounded-xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 hover:border-stone-400 dark:hover:border-stone-600 cursor-pointer transition shadow-2xs space-y-2 group"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <span className="font-bold text-xs text-stone-900 dark:text-stone-100 group-hover:text-stone-700 dark:group-hover:text-stone-300">
                          {meta.name}
                        </span>
                        <span className="px-1.5 py-0.2 rounded text-[9px] font-mono font-bold uppercase bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400 border border-stone-200 dark:border-stone-700">
                          {c.agent_type}
                        </span>
                      </div>
                      <ChevronRight className="w-3.5 h-3.5 text-stone-400 group-hover:translate-x-0.5 transition-transform" />
                    </div>
                    <p className="text-[11px] text-stone-500 dark:text-stone-400 line-clamp-1">
                      {meta.role}
                    </p>
                    <div className="flex items-center justify-between gap-1 text-[11px] font-mono pt-1.5 border-t border-stone-100 dark:border-stone-800/60">
                      <div className="flex items-center gap-1.5 min-w-0 truncate text-stone-700 dark:text-stone-300">
                        <Cpu className="w-3.5 h-3.5 shrink-0 text-stone-400 dark:text-stone-500" />
                        <span className="truncate font-medium" title={modelIntel.id}>
                          {modelIntel.name}
                        </span>
                      </div>
                      <span
                        className="shrink-0 text-[10px] text-emerald-600 dark:text-emerald-400 font-semibold bg-emerald-50 dark:bg-emerald-950/40 px-1.5 py-0.5 rounded border border-emerald-200/60 dark:border-emerald-800/40"
                        title="Prompt (input) / Completion (output) price per 1M tokens"
                      >
                        {priceDisplay}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
